import { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showError } from "../../../../../redux/common/actions";
import { getSiteAssetRegisters } from "services/clients/sites/siteAssetRegisters";

const BATCH_SIZE = 10;

const useTreeExpansion = (siteId, transformData, childDataRef, childFilter) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [bulkLoading, setBulkLoading] = useState(false);

	const fetchChildrenBatch = useCallback(
		async (parentIds, setChildData, setChildLoading) => {
			const uncachedIds = parentIds.filter(
				(id) => !childDataRef.current[id]
			);

			if (uncachedIds.length === 0) {
				const results = {};
				parentIds.forEach((id) => {
					results[id] = childDataRef.current[id];
				});
				return results;
			}

			setChildLoading((prev) => {
				const next = { ...prev };
				uncachedIds.forEach((id) => { next[id] = true; });
				return next;
			});

			const newData = {};

			for (let i = 0; i < uncachedIds.length; i += BATCH_SIZE) {
				const chunk = uncachedIds.slice(i, i + BATCH_SIZE);
				const promises = chunk.map(async (parentId) => {
					try {
						const response = await getSiteAssetRegisters(
							siteId, null, null, "", "", "", parentId
						);
						if (response.status) {
							const children = transformData(response.data);
							newData[parentId] = childFilter ? childFilter(parentId, children) : children;
						} else {
							newData[parentId] = [];
						}
					} catch {
						newData[parentId] = [];
					}
				});
				await Promise.all(promises);
			}

			setChildData((prev) => ({ ...prev, ...newData }));
			childDataRef.current = { ...childDataRef.current, ...newData };

			setChildLoading((prev) => {
				const next = { ...prev };
				uncachedIds.forEach((id) => { next[id] = false; });
				return next;
			});

			const results = {};
			parentIds.forEach((id) => {
				results[id] = newData[id] || childDataRef.current[id] || [];
			});
			return results;
		},
		[siteId, transformData, childDataRef, childFilter]
	);

	const expandAll = useCallback(
		async (rootData, setExpandedRows, setChildData, setChildLoading) => {
			setBulkLoading(true);
			try {
				const allExpanded = {};
				let currentWave = rootData
					.filter((r) => r.hasChildren)
					.map((r) => r.id);

				while (currentWave.length > 0) {
					currentWave.forEach((id) => { allExpanded[id] = true; });

					const batchResults = await fetchChildrenBatch(
						currentWave, setChildData, setChildLoading
					);

					const nextWave = [];
					Object.values(batchResults).forEach((children) => {
						children.forEach((child) => {
							if (child.hasChildren) nextWave.push(child.id);
						});
					});
					currentWave = nextWave;
				}

				setExpandedRows((prev) => ({ ...prev, ...allExpanded }));
			} catch {
				dispatch(showError(t("message.failedToExpandAllNodes")));
			} finally {
				setBulkLoading(false);
			}
		},
		[fetchChildrenBatch, dispatch, t]
	);

	const collapseAll = useCallback((setExpandedRows) => {
		setExpandedRows({});
	}, []);

	return { bulkLoading, expandAll, collapseAll };
};

export default useTreeExpansion;
